import React, { useState, useRef, useEffect, forwardRef } from 'react'
import PropTypes from 'prop-types'
import { formatAndSanitizeHTML } from 'utils/misc'
import SurveyQuestion from '../SurveyQuestion'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion
} from '../../utils/instrumentUtils'
import { Button, DialogueBox } from 'components/UIElements'
import trySound from 'assets/sounds/try-sound.mp3'

const TIMER_SOUND_LEVEL =
  "Make sure your sound is on and adjust your audio to a confortable level using your device's volume controls."
const PLAY_SOUND = 'Play sound'
const START_LABEL1 = 'Press "START" when you are ready to start the timer.'
const START_LABEL2 = 'You will hear an audible alert when the timer ends.'
const START = 'START'
const RESET = 'RESET'
const PAUSE = 'PAUSE'
const RESUME = 'RESUME'
const END = 'END'

const persistTimerSubmission = (props, answerArr) => {
  try {
    const { question, id, webSurvey, completeQuestion } = props
    if (typeof completeQuestion !== 'function' || !webSurvey) return
    const { label, type, attributes } = question
    const { required } = attributes
    const answer = Array.isArray(answerArr) ? answerArr : []
    const hasAnswer = answer.length > 0
    const submission = {
      id,
      label,
      name: id,
      type,
      required,
      state: hasAnswer ? 'ANSWERED' : 'SKIPPED',
      answer,
    }
    completeQuestion(submission, webSurvey.order?.[0])
  } catch (e) {
    console.warn('Timer autosave failed', { error: e })
  }
}

const completeTimerQuestion = (props, answerArr) => {
  const {
    question,
    id,
    completeQuestion,
    nextQ,
    webSurvey,
    submissionSurvey,
    nextQId,
    reviewFunctionalitySupported,
    isConsent = false,
    isLastQuestion = false,
    isSibling = false,
  } = props
  const { label, type, attributes } = question
  const { required } = attributes
  const answer = Array.isArray(answerArr) ? answerArr : []
  const hasAnswer = answer.length > 0
  const submission = {
    id,
    label,
    name: id,
    type,
    required,
    state: hasAnswer ? 'ANSWERED' : 'SKIPPED',
    answer,
  }
  const currentAnswer = { [id]: answer }

  const shouldRenderNextQ = getShouldRenderNextQuestion({
    isConsent,
    isLastQuestion,
    isSibling,
  })

  checkLogicAndComplete(
    webSurvey,
    id,
    submissionSurvey,
    currentAnswer,
    nextQId,
    nextQ,
    completeQuestion,
    submission,
    reviewFunctionalitySupported,
    shouldRenderNextQ
  )
}

const AudioLevelDialogue = ({
  audioLevelGood,
  title,
  playSound,
  onPlaySound,
  onAudioLevelOk
}) => (
  <DialogueBox className={`timer-audio`}>
    <div className='scroll-container'>
      <h3>{title}</h3>
      <Button
        className='play-sound'
        link
        onClick={onPlaySound}
        icon='fa fa-play'
        content={playSound}
      />
      <Button
        primary
        className='audio-ok'
        onClick={onAudioLevelOk}
        content={audioLevelGood}
      />
    </div>
  </DialogueBox>
)

const TimerLoader = ({
  strings,
  audioAlert,
  onAudioLevelOk,
  isAudioOk,
  minutes,
  seconds,
  isCountdown,
  isTimeComplete,
  setTimeCompleted,
  disablePauseResume,
  updateTimerState
}) => {
  const audioRef = useRef(null)

  const [time, setTime] = useState({
    minutes: isCountdown ? minutes : 0,
    seconds: isCountdown ? seconds : 0
  })
  const [isActive, setIsActive] = useState(false)
  const [isPaused, setIsPaused] = useState(false)

  useEffect(() => {
    let timeInterval = null

    if (isActive && !isPaused) {
      timeInterval = setInterval(() => {
        setTime(prevTime => {
          let updatedMinutes = prevTime.minutes
          let updatedSeconds = prevTime.seconds

          if (isCountdown) {
            // Countdown logic
            if (updatedSeconds === 0) {
              if (updatedMinutes === 0) {
                clearInterval(timeInterval)
                queueMicrotask(() => {
                  if (audioAlert) onPlaySound()
                  setTimeCompleted(true)
                  updateTimerState(END)
                })
              } else {
                updatedMinutes -= 1
                updatedSeconds = 59
              }
            } else {
              updatedSeconds -= 1
            }
          } else {
            // Count up logic
            if (updatedSeconds === 59) {
              updatedMinutes += 1
              updatedSeconds = 0
            } else {
              updatedSeconds += 1
            }
            if (updatedMinutes === minutes && updatedSeconds === seconds) {
              clearInterval(timeInterval)
              queueMicrotask(() => {
                if (audioAlert) onPlaySound()
                setTimeCompleted(true)
                updateTimerState(END)
              })
            }
          }

          return { minutes: updatedMinutes, seconds: updatedSeconds }
        })
      }, 1000)
    }

    return () => {
      clearInterval(timeInterval)
    }
  }, [isActive, isPaused, isCountdown])

  const formatTime = value => (value < 10 ? `0${value}` : value)

  const onPlaySound = () => {
    audioRef.current.play()
  }

  const onStartTimer = () => {
    if (!isActive) {
      // Start
      setIsActive(true)
      updateTimerState(strings.start || START)
    } else {
      // Reset
      setIsActive(false)
      setIsPaused(false)
      setTime({
        minutes: isCountdown ? minutes : 0,
        seconds: isCountdown ? seconds : 0
      })
      setTimeCompleted(false)
      updateTimerState(strings.reset || RESET)
    }
  }

  const onResumeTimer = () => {
    updateTimerState(
      !isPaused ? strings.pause || PAUSE : strings.resume || RESUME
    )
    setIsPaused(!isPaused)
  }

  const totalTime = minutes * 60 + seconds
  const timeLeft = time.minutes * 60 + time.seconds
  const percentage =
    ((isCountdown ? totalTime - timeLeft : timeLeft) / totalTime) * 100

  return (
    <div className='timer-container'>
      <audio ref={audioRef} src={trySound} />
      {audioAlert && !isAudioOk ? (
        <>
          <AudioLevelDialogue
            audioLevelGood={strings.audio_level_ok || 'Audio level is good!'}
            title={strings.timer_audio_level || TIMER_SOUND_LEVEL}
            playSound={strings.play_sound || PLAY_SOUND}
            onPlaySound={onPlaySound}
            onAudioLevelOk={onAudioLevelOk}
          />
        </>
      ) : null}
      {isAudioOk ? (
        <div className='time-handler'>
          <h3>{strings.timer_start_label1 || START_LABEL1}</h3>
          {audioAlert ? (
            <h3>{strings.timer_start_label2 || START_LABEL2}</h3>
          ) : null}
          <Button
            className='start-timer'
            content={
              !isActive ? strings.start || START : strings.reset || RESET
            }
            onClick={onStartTimer}
          />
          {isActive && !disablePauseResume && !isTimeComplete ? (
            <Button
              className='resume-timer'
              content={
                isPaused ? strings.resume || RESUME : strings.pause || PAUSE
              }
              onClick={onResumeTimer}
            />
          ) : null}
          <div className='timer-loader-container'>
            <div
              className='circle'
              style={{
                background: `conic-gradient(#ffc107 ${percentage}%, #e0e0e0 ${percentage}%)`
              }}></div>
            <h1 className='time-display'>
              {formatTime(time.minutes)}:{formatTime(time.seconds)}
            </h1>
          </div>
        </div>
      ) : null}
    </div>
  )
}

const Timer = forwardRef((props, ref) => {
  const {
    className,
    currentSurvey,
    id,
    isConsent,
    isLastQuestion,
    question,
    renderBackButton,
    setAllRequiredAnswered,
    strings,
    storedAnswer,
    submissionLoading,
    submissionSurvey,
    webSurvey,
    isSibling = false
  } = props

  const { attributes, label } = question
  const { required, timer_details } = attributes
  const {
    time_string,
    time,
    countdown,
    audio_alert,
    disable_pause_resume
  } = timer_details
  const _className = className ? ` ${className}` : ''

  const [isTimeComplete, setTimeCompleted] = useState(false)
  const [timerStates, setTimerState] = useState([])
  const [isAudioOk, setAudioOk] = useState(!audio_alert)

  useEffect(() => {
    const raw = storedAnswer?.[0]?.value
    if (raw == null || raw === '') return
    let restored = []
    if (Array.isArray(raw)) {
      restored = raw
    } else if (typeof raw === 'string' && raw !== '') {
      try {
        restored = JSON.parse(raw)
      } catch {
        restored = []
      }
    }
    if (!Array.isArray(restored)) restored = []
    setTimeCompleted(true)
    if (restored.length) setTimerState(restored)
    if (!props.suppressAutoAdvance) {
      completeTimerQuestion(props, restored)
    }
  }, [])

  useEffect(() => {
    if (!isTimeComplete) return
    persistTimerSubmission(props, timerStates)
  }, [isTimeComplete, timerStates])

  const timeSplited = time_string.split(':')

  const onAudioOk = () => {
    setAudioOk(true)
  }

  const updateTimerState = btnType => {
    setTimerState(prev => [
      ...prev,
      { type: btnType.toLowerCase(), value: new Date() },
    ])
  }

  return (
    <SurveyQuestion id={id}>
      <MakeScrollable>
        <div ref={ref} className={`survey-question timer${_className}`}>
          <p className='question-label'>
            <span
              dangerouslySetInnerHTML={{
                __html: formatAndSanitizeHTML(label)
              }}
            />
          </p>
          <TimerLoader
            strings={strings}
            audioAlert={audio_alert}
            isAudioOk={isAudioOk}
            onAudioLevelOk={onAudioOk}
            minutes={Number(timeSplited[0])}
            seconds={Number(timeSplited[1])}
            isCountdown={countdown}
            isTimeComplete={isTimeComplete}
            setTimeCompleted={setTimeCompleted}
            disablePauseResume={disable_pause_resume}
            updateTimerState={updateTimerState}
          />
          <div className='character-count-row'>
            {!isSibling && (
              <QuestionFooter
                completed={isTimeComplete}
                id={id}
                isConsent={isConsent}
                isLastQuestion={isLastQuestion}
                loading={submissionLoading}
                onComplete={() => {
                  if (isTimeComplete) completeTimerQuestion(props, timerStates)
                }}
                question={question}
                renderBackButton={renderBackButton}
                strings={strings}
                submissionSurvey={submissionSurvey}
                webSurvey={webSurvey}
              />
            )}
          </div>
        </div>
      </MakeScrollable>
    </SurveyQuestion>
  )
})

export default Timer

Timer.propTypes = {
  className: PropTypes.string,
  currentSurvey: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string)
}
